<script setup lang="ts">
  import { onBeforeMount } from 'vue';
  import ModalDialog from '@/VueComponents/Modals/ModalDialog.vue';
  import FileSizeFormatter from '@/Utils/fileSizeFormatter';
  import resourceHelper from '@/Utils/resourceHelper';
  import FilePathFormatter from '@/Utils/filePathFormatter';
  import UploadSelectorFileUploadModel from '@/VueComponents/FileUploadSelector/models/uploadSelectorFileUploadModel';
  import useResetableState from '@/VueCore/utils/useResetableState';

  const props = defineProps<{
    duplicatedFiles?: UploadSelectorFileUploadModel[],
    nonDuplicatedFiles?: UploadSelectorFileUploadModel[]
  }>();

  const emit = defineEmits<{'continue': [UploadSelectorFileUploadModel[]]}>();
  const visible = defineModel<boolean>({ default: false });
  const maxRenderedItemsCount: number = 100;

  const { state, resetState } = useResetableState(() => ({
    uploadDuplicatesDisclaimer: resourceHelper.getString('UploadDuplicatesDisclaimer', { count: props.duplicatedFiles?.length ?? 0 }),
    filesToRender: props.duplicatedFiles?.slice(0, maxRenderedItemsCount).map(f => f.file)
  }));


  function onContinue() {
    const files = [...props.duplicatedFiles!, ...props.nonDuplicatedFiles!];
    emit('continue', files);
  }

  function onSkipDuplicates() {
    emit('continue', props.nonDuplicatedFiles!);
  }

  function formatFileSize(bytes: number): string {
    return FileSizeFormatter.getFormattedFileSize(bytes);
  }
</script>

<template>
  <ModalDialog
    v-model="visible"
    :title="$localize('ListOfDuplicatedFiles')"
    :is-danger="false"
    @open="resetState"
  >
    <template #default>
      <p>{{ state.uploadDuplicatesDisclaimer }}</p>
      <div
        class="row vertical-align"
      >
        <div class="col col-xs-4">
          <strong>{{ $localize('FileName') }}</strong>
        </div>
        <div class="col col-xs-2">
          <strong>{{ $localize('FileSize') }}</strong>
        </div>
        <div class="col col-xs-6">
          <strong>{{ $localize('FileRelativePath') }}</strong>
        </div>
      </div>
      <div
        id="table-data"
        class="table-data"
      >
        <div
          v-for="file in state.filesToRender"
          :key="file.name"
          class="row vertical-align table-data--row"
        >
          <div class="col col-xs-4 ellipsis">
            <span :title="file.name">
              {{ file.name }}
            </span>
          </div>
          <div class="col col-xs-2 ellipsis">
            <span :title="formatFileSize(file.size)">
              {{ formatFileSize(file.size) }}
            </span>
          </div>
          <div class="col col-xs-6 ellipsis">
            <span :title="FilePathFormatter.getFilePathWithoutFileName(file.webkitRelativePath)">
              {{ FilePathFormatter.getFilePathWithoutFileName(file.webkitRelativePath) }}</span>
          </div>
        </div>
      </div>
      <strong v-if="(duplicatedFiles?.length ?? 0) > maxRenderedItemsCount">
        {{ $localize('FirstHundredOfDuplicatesIsShown') }}
      </strong>
    </template>
    <template #footer>
      <button
        ref="closeBtn"
        class="btn btn-outline-secondary"
        @click="visible = false"
      >
        {{ $localize('Cancel') }}
      </button>
      <button
        class="btn btn-outline-secondary"
        @click="onContinue"
      >
        {{ $localize('Continue') }}
      </button>
      <button
        class="btn btn-primary"
        @click="onSkipDuplicates"
      >
        {{ $localize('SkipDuplicates') }}
      </button>
    </template>
  </ModalDialog>
</template>

<style lang="scss" scoped>
@use "sass/site/_modal-sizes.scss";
@import "sass/site/_colours.scss";

    .table-data {
        max-height: modal-sizes.$modal-dialog-main-control-common-height;
        overflow-y: auto;
        overflow-x: hidden;

        &--row:nth-child(even) {
            background-color: $background-light-grey;
        }
    }
</style>